import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { PackageDisplay } from '../../../components/PackageDisplay';
import { SubscriptionNotification } from './components';
import { useNetwork } from '../../../utils/network';
import { SubscriptionPackagesType } from '../../../types/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShowSnackbar } from '../../../utils/snackbar';
import { useUser } from '../../../contexts/UserContext';
import GiveawayPrizeTimers from '../../Home/DynamicHomepage/components/GiveawayPrizeTimers/GiveawayPrizeTimers';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../../utils/subscriptionDiscounts';

interface PackageRarity {
  rarity: string;
  guaranteed: boolean;
  pullable: boolean;
}

interface PackageDetails {
  id: number;
  name: string;
  type: string;
  description: string;
  imageUrl: string;
  price: string;
  cards: number;
  tokens: number;
  entries: number;
  purchaseText?: string;
  isDisabled?: boolean;
  PackageRarities: PackageRarity[];
}

type SubscriptionProps = {
  handlePurchaseSelection: (purchasePackage: SubscriptionPackagesType) => void;
};

export const Subscription = ({
  handlePurchaseSelection
}: SubscriptionProps) => {
  const { get } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const { user, setUser, userCurrentSubscription, setUserCurrentSubscription } =
    useUser();
  const [allSubscriptions, setAllSubscriptions] = useState<PackageDetails[]>(
    []
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [subscriptionChanged, setSubscriptionChanged] = useState(false);
  const [hasUserEverSubscribed, setHasUserEverSubscribed] = useState<boolean>();

  useEffect(() => {
    const getSubscriptionPackages = async () => {
      try {
        const result = await get('/package/subscription', true);
        setAllSubscriptions(result);
      } catch (error: any) {
        console.error('Error fetching subscription packages:', error);
      }
    };

    getSubscriptionPackages();
  }, [subscriptionChanged]);

  useEffect(() => {
    const getCurrentSubscription = async () => {
      try {
        const result = await get('user/subscription', true);
        setUserCurrentSubscription({
          package: result.package,
          endDate: result.endDate
        });
      } catch (error: any) {
        console.log(error);
      }
    };

    const getHasUserSubscribedBefore = async () => {
      try {
        const result = await get('user/subscription-check', true);
        setHasUserEverSubscribed(result.hasEverSubscribed);
      } catch (error: any) {
        console.log(error);
      }
    };

    getHasUserSubscribedBefore();
    getCurrentSubscription();
  }, []);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      const searchParams = new URLSearchParams(location.search);
      const paymentIntent = searchParams.get('payment_intent') ?? '';
      const paymentIntentClientSecret = searchParams.get(
        'payment_intent_client_secret'
      );
      const redirectStatus = searchParams.get('redirect_status');
      const paymentSuccess = searchParams.get('payment_success');
      const packageName = searchParams.get('package_name');

      // Handle payment status (both new and legacy methods)
      if (redirectStatus === 'succeeded' || paymentSuccess === 'true') {
        const message = packageName
          ? `🎉 Subscription activated! Welcome to ${decodeURIComponent(packageName)}!`
          : 'Payment Successful! Your subscription is now active!';

        showSnackbar(message, 'success');
        setUser({
          ...user,
          hasActiveSubscription: true
        });
        setSubscriptionChanged((prev) => !prev);
      } else if (redirectStatus === 'failed') {
        showSnackbar('Payment failed. Please try again.', 'error');
      }

      // Clean up the URL once handled
      if (
        paymentIntent ||
        paymentIntentClientSecret ||
        redirectStatus ||
        paymentSuccess
      ) {
        navigate(location.pathname, { replace: true });
      }
    };

    fetchPaymentDetails();
  }, [location, navigate, showSnackbar, user, setUser]);

  const getNumericPrice = (price: string | number) => Number(price || 0);

  const highestTierPackage = useMemo(() => {
    if (!allSubscriptions.length) return null;

    return [...allSubscriptions].sort(
      (a, b) => getNumericPrice(b.price) - getNumericPrice(a.price)
    )[0];
  }, [allSubscriptions]);

  const getStartingBonusEntries = (pack: PackageDetails) =>
    Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1, pb: 2 }}>
        <Typography
          sx={{
            font: '800 10px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: '#ffcf40'
          }}
        >
          Membership
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontWeight: 900,
            fontSize: 'clamp(24px, 3.4vw, 36px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#e8f4fb'
          }}
        >
          {userCurrentSubscription.package ? 'Manage your ' : 'Level up your '}
          <Box component="span" sx={{ color: '#38e8ff' }}>
            membership
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            font: '600 13px/1.55 ui-monospace, Menlo, Consolas, monospace',
            color: '#8ea3b3',
            maxWidth: 640
          }}
        >
          Subscribe for monthly INC, digital assets, and stacking entries into
          every $1,000 subscriber giveaway.
        </Typography>
      </Box>

      {/* Current plan and the draw timers sit side by side once there is room for
          both (lg+): the plan is the "where you are", the timers the "what it gets
          you", and on a wide screen stacking them buried the timers below the fold.
          Below lg they stack in the original order — mobile is unchanged. */}
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          mb: 4,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) minmax(0, 1.1fr)'
          },
          gap: { xs: 4, lg: 2 },
          alignItems: 'stretch'
        }}
      >
        <SubscriptionNotification
          currentSubscription={userCurrentSubscription}
        />

        <GiveawayPrizeTimers
          title=""
          jackpot
          jackpotHeading="Gain access to stacking entries into the A$10,000 giveaway"
          // this block shares the row with the plan panel on wide screens, so the
          // $1,000 draws go one per row rather than three across a half-width column
          stackDraws
          subtitle={
            userCurrentSubscription.package
              ? 'On top of the A$10,000 giveaway, your subscription stacks entries into our A$1,000 giveaways — draws only subscribers can enter.'
              : 'On top of the A$10,000 giveaway, subscribers get stacking entries into our A$1,000 giveaways — and only subscribers can enter those draws.'
          }
          // Without this the component renders nothing when no draws are running,
          // which on the two-column layout would leave the plan panel stranded at
          // half width beside an empty cell.
          emptyText="Draw dates for the next subscriber giveaways are announced here as soon as they open."
          compact
          tone="dark"
        />
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'center', px: { xs: 1, md: 2 } }}
      >
        <Grid
          container
          rowSpacing={4}
          sx={{ width: '100%', maxWidth: '100%', m: 0 }}
        >
          {allSubscriptions.map((pack, index) => {
            const packPrice = parseFloat(pack.price);
            const currentSubscriptionPrice = userCurrentSubscription.package
              ? parseFloat(userCurrentSubscription.package.price)
              : 0;

            const isTopTier = highestTierPackage?.id === pack.id;
            const discountedSubscriptionPrice =
              isDiscountEligibleSubscriptionPackage(pack.id)
                ? getFirstMonthDiscountedPrice(packPrice).toFixed(2)
                : null;

            let derivedPack = {
              ...pack,
              purchaseText: pack.purchaseText,
              isDisabled: pack.isDisabled
            };

            if (
              userCurrentSubscription.package &&
              userCurrentSubscription.endDate === null
            ) {
              if (packPrice > currentSubscriptionPrice) {
                derivedPack.purchaseText = 'Upgrade';
              } else if (packPrice < currentSubscriptionPrice) {
                derivedPack.purchaseText = 'Higher Tier Owned';
                derivedPack.isDisabled = true;
              } else {
                derivedPack.purchaseText = 'Purchased';
                derivedPack.isDisabled = true;
              }
            } else if (
              userCurrentSubscription.package &&
              userCurrentSubscription.endDate !== null
            ) {
              derivedPack.purchaseText = 'Active Subscription Still in Effect';
              derivedPack.isDisabled = true;
            }

            const canReceiveStartingBonus =
              derivedPack.type === 'Subscription' &&
              !derivedPack.isDisabled &&
              (!userCurrentSubscription.package ||
                derivedPack.purchaseText === 'Upgrade');

            return (
              <Grid xs={12} sm={6} md={4} key={index}>
                <Box sx={{ px: { xs: 0, sm: 1 } }}>
                  <PackageDisplay
                    packageData={derivedPack}
                    handlePurchaseSelection={handlePurchaseSelection}
                    isTopTier={isTopTier}
                    discountedSubscriptionPrice={discountedSubscriptionPrice}
                    startingBonusEntries={
                      canReceiveStartingBonus
                        ? getStartingBonusEntries(derivedPack)
                        : 0
                    }
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};
